import React, { useState, useRef, useEffect } from 'react';
import { 
  X, Send, CheckCircle2, MessageSquare, AlertCircle, Loader2, 
  User, Mail, MessageCircle, FileText, PenLine, CloudUpload, ShieldCheck, File
} from 'lucide-react';
import { submitContactMessage } from '../lib/contactCenterService';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';

interface RequestQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  prefilledEmail?: string;
}

export const RequestQuoteModal: React.FC<RequestQuoteModalProps> = ({ isOpen, onClose, prefilledEmail }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: '',
    projectType: '',
    description: '',
    website: '',
  });
  const [file, setFile] = useState<File | null>(null);
  const [consent, setConsent] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({ ...prev, email: prefilledEmail || '', projectType: '' }));
      setIsSuccess(false);
      setErrorMsg('');
      setFile(null);
      setConsent(false);
      
      // Hide Crisp Chat Widget when modal opens
      try {
        if ((window as any).$crisp) {
          (window as any).$crisp.push(['do', 'chat:hide']);
        }
      } catch (e) {}
    } else {
      // Show Crisp Chat Widget when modal closes
      try {
        if ((window as any).$crisp) {
          (window as any).$crisp.push(['do', 'chat:show']);
        }
      } catch (e) {}
    }
    
    return () => {
      // Ensure it shows if component unmounts while open
      if (isOpen) {
        try {
          if ((window as any).$crisp) {
            (window as any).$crisp.push(['do', 'chat:show']);
          }
        } catch (e) {}
      }
    };
  }, [isOpen, prefilledEmail]);

  if (!isOpen) return null;

  const projectTypes = [
    'Certificate',
    'Driving License',
    'ID Card',
    'Bank Statement',
    'Pay Stub',
    'Passport',
    'Visa',
    'Other Document',
    'Other Project'
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      
      // Strict client-side validation
      if (!validTypes.includes(selected.type)) {
        setErrorMsg('Invalid file type. Only JPG, PNG, WEBP, and PDF formats are permitted for security reasons.');
        return;
      }
      
      // Strict size validation (Max 10MB)
      if (selected.size > 10 * 1024 * 1024) {
        setErrorMsg('File size exceeds the maximum permitted limit of 10MB.');
        return;
      }
      
      setFile(selected);
      setErrorMsg('');
    }
  };

  const uploadFileToStorage = async (fileToUpload: File): Promise<string> => {
    try {
      // Sanitize file name for security
      const sanitizedName = fileToUpload.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
      const storagePath = `quote_requests/${Date.now()}_${sanitizedName}`;
      const fileRef = ref(storage, storagePath);
      await uploadBytes(fileRef, fileToUpload);
      // We return a proprietary storage URI so that only Admins can securely view it via the dashboard,
      // avoiding the need for public read access which compromises security.
      return `storage://${storagePath}`;
    } catch (err: any) {
      console.error('File upload error:', err);
      // Graceful error for unconfigured buckets or strict rules
      if (err.code === 'storage/unauthorized') {
        throw new Error('Upload blocked by server security rules. Please contact support.');
      } else if (err.code === 'storage/object-not-found' || err.message.includes('bucket')) {
        throw new Error('File storage is currently unavailable. Please try again later.');
      }
      throw new Error('Failed to upload file securely. Please check your network or contact support.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.website) {
      setErrorMsg('Your request was flagged as spam.');
      return;
    }
    if (!formData.name || !formData.email || !formData.contact || !formData.projectType || !formData.description) {
      setErrorMsg('Please fill in all required fields.');
      return;
    }
    if (!consent) {
      setErrorMsg('You must consent to the terms to submit.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg('');

    try {
      let fileUrl = '';
      if (file) {
        fileUrl = await uploadFileToStorage(file);
      }

      const subject = `Quote Request: ${formData.projectType}`;
      const messageBody = `
        Contact Method: ${formData.contact}
        Project Type: ${formData.projectType}
        
        Description:
        ${formData.description}
        
        ${fileUrl ? `Attached Reference File: ${fileUrl}` : ''}
      `;

      await submitContactMessage({
        name: formData.name,
        email: formData.email,
        subject: subject,
        category: 'Quote Request',
        message: messageBody,
      });
      
      setIsSuccess(true);
    } catch (err: any) {
      setErrorMsg(err.message || 'An error occurred while submitting your request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`fixed inset-0 z-[60] flex flex-col items-center overflow-hidden transition-all duration-500 ${isSuccess ? 'justify-center p-4 sm:p-6' : 'px-3.5 pt-[76px] pb-4 sm:px-6 sm:pt-[104px] sm:pb-8 md:px-8 md:pt-[112px] md:pb-10'}`}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-[#020617]/95 transition-opacity" 
        onClick={() => !isSubmitting && onClose()} 
      />
      
      {/* Modal Wrapper for Gradient Border */}
      <div 
        className={`relative w-full ${isSuccess ? 'max-w-[480px] my-auto' : 'max-w-[850px]'} p-[1.5px] rounded-[1.75rem] md:rounded-[2rem] bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 shadow-[0_0_40px_rgba(139,92,246,0.15)] flex flex-col min-h-0 max-h-full animate-in fade-in zoom-in-95 transition-all duration-500`}
        role="dialog"
        aria-modal="true"
      >
        {/* Inner Modal Content */}
        <div className="bg-[#0B101E] rounded-[calc(1.75rem-1.5px)] md:rounded-[calc(2rem-1.5px)] w-full flex flex-col min-h-0 overflow-hidden relative">
          
          {/* Close Button */}
          <button 
            onClick={onClose}
            disabled={isSubmitting}
            className="absolute top-4 right-4 sm:top-5 sm:right-5 p-2 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors z-10 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-5 sm:p-7 md:p-8 overflow-y-auto custom-scrollbar flex-1 min-h-0">
            {isSuccess ? (
              <div className="flex flex-col items-center justify-center py-12 text-center space-y-5">
                <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center border border-emerald-500/30">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Request Received Successfully!</h3>
                  <p className="text-slate-300 max-w-lg mx-auto leading-relaxed text-[15px]">
                    Thank you for contacting RH Editography. We have received your request and will review the details. Our team will contact you using your preferred contact information.
                  </p>
                </div>
                <button 
                  onClick={onClose}
                  className="mt-4 px-8 py-3.5 rounded-xl bg-slate-800 text-white font-semibold hover:bg-slate-700 transition-colors text-[15px]"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="mb-5 sm:mb-6 flex items-start gap-3 sm:gap-4 pr-8 sm:pr-10">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#131B2C] border border-indigo-500/30 flex items-center justify-center shadow-[0_0_15px_rgba(99,102,241,0.2)] flex-shrink-0">
                    <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-400" />
                  </div>
                  <div>
                    <h2 className="text-[22px] sm:text-[26px] font-bold text-white mb-1 sm:mb-1.5 tracking-tight leading-tight">Request a Custom Quote</h2>
                    <p className="text-[13px] sm:text-[14px] text-slate-300 leading-relaxed max-w-xl">
                      Tell us about your project and our team will review your request and contact you with the next steps.
                    </p>
                  </div>
                </div>

                {errorMsg && (
                  <div className="mb-4 sm:mb-5 p-3 sm:p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-start gap-2 sm:gap-3">
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                    <p className="text-[13px] sm:text-[14px] text-rose-300 font-medium">{errorMsg}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                  {/* Honeypot */}
                  <div className="hidden" aria-hidden="true">
                    <label>Website</label>
                    <input 
                      type="text" 
                      name="website" 
                      tabIndex={-1} 
                      autoComplete="off" 
                      value={formData.website}
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                    />
                  </div>
                  
                  {/* First Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                    {/* Full Name */}
                    <div className="space-y-1 sm:space-y-1.5">
                      <label className="text-[13px] sm:text-[14px] font-medium text-slate-200">
                        Full Name <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                          <User className="w-[18px] h-[18px] text-slate-400" />
                        </div>
                        <input 
                          type="text" 
                          required
                          placeholder="e.g. Alex Rivera"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="w-full h-11 sm:h-12 pl-10 pr-4 rounded-xl bg-[#131B2C] border border-slate-700/80 text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all text-[14px]"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div className="space-y-1 sm:space-y-1.5">
                      <label className="text-[13px] sm:text-[14px] font-medium text-slate-200">
                        Email Address <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                          <Mail className="w-[18px] h-[18px] text-slate-400" />
                        </div>
                        <input 
                          type="email" 
                          required
                          placeholder="you@example.com"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="w-full h-11 sm:h-12 pl-10 pr-4 rounded-xl bg-[#131B2C] border border-slate-700/80 text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all text-[14px]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Second Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                    {/* WhatsApp/Telegram */}
                    <div className="space-y-1 sm:space-y-1.5">
                      <label className="text-[13px] sm:text-[14px] font-medium text-slate-200">
                        WhatsApp or Telegram <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                          <MessageCircle className="w-[18px] h-[18px] text-slate-400" />
                        </div>
                        <input 
                          type="text" 
                          required
                          placeholder="WhatsApp or Telegram username"
                          value={formData.contact}
                          onChange={(e) => setFormData({...formData, contact: e.target.value})}
                          className="w-full h-11 sm:h-12 pl-10 pr-4 rounded-xl bg-[#131B2C] border border-slate-700/80 text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all text-[14px]"
                        />
                      </div>
                      <p className="text-[11px] sm:text-[12px] text-slate-400 mt-1">Provide your preferred contact method.</p>
                    </div>

                    {/* Project Type */}
                    <div className="space-y-1 sm:space-y-1.5">
                      <label className="text-[13px] sm:text-[14px] font-medium text-slate-200">
                        Document / Project Type <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none z-10">
                          <File className="w-[18px] h-[18px] text-slate-400" />
                        </div>
                        <select
                          required
                          value={formData.projectType}
                          onChange={(e) => setFormData({...formData, projectType: e.target.value})}
                          className="w-full h-11 sm:h-12 pl-10 pr-4 rounded-xl bg-[#131B2C] border border-slate-700/80 text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all appearance-none text-[14px]"
                        >
                          <option value="" disabled>Select document or project type</option>
                          {projectTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                        {/* Custom Dropdown Arrow */}
                        <div className="absolute inset-y-0 right-0 flex items-center px-3.5 pointer-events-none">
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-1 sm:space-y-1.5">
                    <label className="text-[13px] sm:text-[14px] font-medium text-slate-200">
                      Editing Description <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute top-3.5 left-3.5 pointer-events-none">
                        <PenLine className="w-[18px] h-[18px] text-slate-400" />
                      </div>
                      <textarea 
                        required
                        placeholder="Please describe what you need and provide details about your requested changes..."
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full h-[100px] sm:h-[120px] pl-10 pr-4 py-3 rounded-xl bg-[#131B2C] border border-slate-700/80 text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all resize-none text-[14px]"
                      />
                    </div>
                  </div>

                  {/* File Upload */}
                  <div className="space-y-1 sm:space-y-1.5 pt-1">
                    <label className="text-[13px] sm:text-[14px] font-medium text-slate-200 flex items-baseline gap-2">
                      Attach Reference File <span className="text-slate-400 font-normal text-[12px] sm:text-[13px]">(Optional)</span>
                    </label>
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-[76px] sm:h-[86px] border-2 border-dashed border-slate-700/80 hover:border-indigo-500/50 rounded-xl px-3 sm:px-4 flex items-center gap-3 sm:gap-4 cursor-pointer transition-all bg-[#131B2C]"
                    >
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        className="hidden" 
                        accept=".jpg,.jpeg,.png,.webp,.pdf"
                        onChange={handleFileChange}
                      />
                      
                      {file ? (
                        <div className="flex items-center gap-2.5 sm:gap-3 w-full">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30 flex-shrink-0">
                            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] sm:text-[14px] font-medium text-white truncate">{file.name}</p>
                            <p className="text-[11px] sm:text-[12px] text-slate-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                          </div>
                          <button 
                            type="button" 
                            onClick={(e) => { e.stopPropagation(); setFile(null); }}
                            className="p-1 sm:p-1.5 hover:bg-slate-800 rounded-full text-slate-400 hover:text-rose-400 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20 flex-shrink-0 shadow-[0_0_10px_rgba(99,102,241,0.1)]">
                            <CloudUpload className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-400" />
                          </div>
                          <div>
                            <p className="text-[13px] sm:text-[14px] font-medium text-white">Click to browse or drag & drop</p>
                            <p className="text-[11px] sm:text-[12px] text-slate-400 mt-0.5">Max size: 10MB • JPG, PNG, WEBP, PDF</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Consent */}
                  <div className="pt-1.5 sm:pt-2">
                    <label className="flex items-start gap-2.5 sm:gap-3 cursor-pointer group">
                      <div className="relative flex items-center justify-center mt-0.5 flex-shrink-0">
                        <input 
                          type="checkbox" 
                          required
                          checked={consent}
                          onChange={(e) => setConsent(e.target.checked)}
                          className="peer sr-only"
                        />
                        <div className="w-[16px] h-[16px] sm:w-[18px] sm:h-[18px] rounded border border-slate-600 bg-[#131B2C] peer-checked:bg-indigo-500 peer-checked:border-indigo-500 transition-colors" />
                        <CheckCircle2 className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white absolute opacity-0 peer-checked:opacity-100 transition-opacity" />
                      </div>
                      <span className="text-[12px] sm:text-[13px] text-slate-300 leading-relaxed group-hover:text-white transition-colors">
                        I confirm that I have the right to submit the provided information and materials, and that my request is lawful and non-deceptive. <span className="text-rose-500">*</span>
                      </span>
                    </label>
                  </div>

                  {/* Submit & Trust */}
                  <div className="pt-1">
                    <button 
                      type="submit" 
                      disabled={isSubmitting || !consent}
                      className="w-full h-11 sm:h-12 rounded-xl bg-gradient-to-r from-blue-500 via-indigo-500 to-fuchsia-500 text-white font-semibold text-[14px] sm:text-[15px] shadow-[0_4px_20px_rgba(139,92,246,0.3)] hover:shadow-[0_4px_25px_rgba(139,92,246,0.4)] hover:scale-[1.01] active:scale-[0.99] transition-all flex items-center justify-center gap-2 sm:gap-2.5 disabled:opacity-70 disabled:pointer-events-none"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-[16px] h-[16px] sm:w-[18px] sm:h-[18px] animate-spin" />
                          <span>Submitting Request...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-[16px] h-[16px] sm:w-[18px] sm:h-[18px]" />
                          <span>Request My Quote</span>
                        </>
                      )}
                    </button>

                    <div className="mt-3 sm:mt-4 flex items-center justify-center gap-1 sm:gap-1.5 text-slate-400">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      <span className="text-[11px] sm:text-[12px] text-center">Your request is safe with us. We never share your information.</span>
                    </div>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
